<!-- Hero Section -->
<section class="relative bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900 text-white overflow-hidden">
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 left-0 w-1/3 h-1/3 bg-purple-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
    <div class="absolute bottom-0 right-0 w-1/3 h-1/3 bg-indigo-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
  </div>
  
  <div class="container mx-auto px-4 py-24 md:py-32 relative z-10">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div class="text-center lg:text-left">
        <div class="inline-block bg-purple-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
          <span class="text-sm font-medium text-purple-300">
            <i class="bx bx-infinite mr-2"></i>Modern Development Practices
          </span>
        </div>
        
        <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
          DevOps <span class="text-purple-400">Services</span>
        </h1>
        
        <p class="text-xl text-gray-300 mb-8 max-w-2xl">
          Accelerate your software delivery with our comprehensive DevOps solutions. We bridge the gap between development and operations, enabling faster deployments, improved collaboration, and enhanced system reliability.
        </p>
        
        <div class="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
          <a routerLink="/contact" class="bg-gradient-to-r from-purple-600 to-indigo-700 hover:from-purple-700 hover:to-indigo-800 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl inline-flex items-center justify-center">
            <i class="bx bx-phone mr-2"></i>
            Contact Us
          </a>
          <a routerLink="/contact" class="bg-transparent border-2 border-purple-500 text-purple-300 hover:bg-purple-500/10 font-semibold py-3 px-8 rounded-lg transition-all duration-300 inline-flex items-center justify-center">
            <i class="bx bx-phone mr-2"></i>
            Get Consultation
          </a>
        </div>
        
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl">
          <div class="flex items-center">
            <div class="bg-purple-500/20 p-3 rounded-lg mr-4">
              <i class="bx bx-check-circle text-2xl text-purple-400"></i>
            </div>
            <div>
              <div class="font-semibold">Continuous Integration</div>
              <div class="text-sm text-gray-400">Automated CI/CD</div>
            </div>
          </div>
          
          <div class="flex items-center">
            <div class="bg-purple-500/20 p-3 rounded-lg mr-4">
              <i class="bx bx-check-circle text-2xl text-purple-400"></i>
            </div>
            <div>
              <div class="font-semibold">Automated Deployments</div>
              <div class="text-sm text-gray-400">Zero Downtime</div>
            </div>
          </div>
          
          <div class="flex items-center">
            <div class="bg-purple-500/20 p-3 rounded-lg mr-4">
              <i class="bx bx-check-circle text-2xl text-purple-400"></i>
            </div>
            <div>
              <div class="font-semibold">24/7 Monitoring</div>
              <div class="text-sm text-gray-400">Real-time Alerts</div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="relative">
        <div class="relative z-10 rounded-2xl overflow-hidden shadow-2xl border border-gray-700">
          <img src="assets/img/devops-hero.png" alt="DevOps Services" class="w-full h-auto">
        </div>
        
        <div class="absolute -bottom-6 -left-6 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-xl p-5 shadow-xl w-48">
          <div class="text-2xl font-bold">CI/CD</div>
          <div class="text-sm">Pipelines</div>
        </div>
        
        <div class="absolute -top-6 -right-6 bg-gradient-to-br from-indigo-600 to-purple-700 rounded-xl p-5 shadow-xl w-40">
          <div class="text-2xl font-bold">IaC</div>
          <div class="text-sm">Automation</div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Services Section -->
<section class="py-20 bg-slate-950 border-t border-slate-800/60">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-purple-500/10 border border-purple-500/40 text-purple-300 px-4 py-1 rounded-full text-sm font-medium mb-4">
        DevOps Solutions
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Comprehensive <span class="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-400">DevOps Services</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Our end-to-end DevOps services streamline your software development lifecycle, enhance collaboration, and accelerate time-to-market.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <!-- CI/CD Pipeline Setup -->
      <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-purple-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center">
            <i class="bx bx-infinite text-3xl text-white"></i>
          </div>
          <span class="bg-purple-100 text-purple-800 text-xs font-bold px-3 py-1 rounded-full">Core</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">CI/CD Pipeline Setup</h3>
        <p class="text-slate-300 mb-4">
          Automate your software build, test, and deployment processes with robust continuous integration and delivery pipelines.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Automated code builds</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Unit & integration testing</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Automated deployments</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Rollback mechanisms</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Pipeline monitoring</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Security scanning integration</span>
          </li>
        </ul>
      </div>

      <!-- Infrastructure as Code -->
      <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-blue-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center">
            <i class="bx bx-code-curly text-3xl text-white"></i>
          </div>
          <span class="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full">Infrastructure</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Infrastructure as Code</h3>
        <p class="text-slate-300 mb-4">
          Provision and manage your infrastructure through code for consistent, repeatable, and scalable environments.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Terraform & Ansible setup</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Cloud infrastructure provisioning</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Configuration management</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Environment consistency</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Version-controlled infrastructure</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Automated scaling</span>
          </li>
        </ul>
      </div>

      <!-- Monitoring & Logging -->
      <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-green-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center">
            <i class="bx bx-bar-chart-alt-2 text-3xl text-white"></i>
          </div>
          <span class="bg-green-100 text-green-800 text-xs font-bold px-3 py-1 rounded-full">Monitoring</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Monitoring & Logging</h3>
        <p class="text-slate-300 mb-4">
          Gain full visibility into your applications and infrastructure with comprehensive monitoring and logging solutions.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Application performance monitoring</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Infrastructure metrics collection</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Centralized log management</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Real-time alerting</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Dashboard creation</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Incident response workflows</span>
          </li>
        </ul>
      </div>

      <!-- DevSecOps Integration -->
      <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-red-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl flex items-center justify-center">
            <i class="bx bx-lock-alt text-3xl text-white"></i>
          </div>
          <span class="bg-red-100 text-red-800 text-xs font-bold px-3 py-1 rounded-full">Security</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">DevSecOps Integration</h3>
        <p class="text-slate-300 mb-4">
          Embed security practices throughout your development lifecycle with automated security testing and compliance.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Security scanning automation</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Vulnerability assessment</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Compliance monitoring</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Secret management</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Policy as code</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Security incident response</span>
          </li>
        </ul>
      </div>

      <!-- Cloud-Native Development -->
      <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-cyan-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center">
            <i class="bx bx-cloud text-3xl text-white"></i>
          </div>
          <span class="bg-cyan-100 text-cyan-800 text-xs font-bold px-3 py-1 rounded-full">Cloud</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Cloud-Native Development</h3>
        <p class="text-slate-300 mb-4">
          Build and deploy applications optimized for cloud environments with containerization and orchestration.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Containerization with Docker</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Kubernetes orchestration</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Microservices architecture</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Service mesh implementation</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Serverless solutions</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Multi-cloud strategies</span>
          </li>
        </ul>
      </div>

      <!-- DevOps Training & Support -->
      <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-amber-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center">
            <i class="bx bx-group text-3xl text-white"></i>
          </div>
          <span class="bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1 rounded-full">Collaboration</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">DevOps Training & Support</h3>
        <p class="text-slate-300 mb-4">
          Empower your team with DevOps best practices and ongoing support for sustainable transformation.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">DevOps workshops</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Team collaboration training</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Best practices coaching</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">24/7 technical support</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Documentation & knowledge transfer</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-green-500 mr-2"></i>
            <span class="text-sm">Continuous improvement guidance</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</section>

<section class="py-20 bg-slate-950 border-t border-slate-800/60">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block px-4 py-1 rounded-full text-sm font-semibold mb-4 tooling-badge">
        Capabilities & Toolchain
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Production-Grade <span class="text-blue-400">DevOps Tooling</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Integrated pipelines and infrastructure as code with best-in-class tooling for secure, scalable delivery.
      </p>
    </div>
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
      <div class="tool-card">
        <div class="tool-header">
          <div class="tool-icon">
            <i class="bx bxl-git"></i>
          </div>
          <span class="tool-badge">Version Control</span>
        </div>
        <div class="tool-title">Git</div>
        <p class="tool-desc">Branching strategies, protected merges, semantic versioning.</p>
        <div class="tool-tags">
          <span>Git Flow</span><span>PR Checks</span><span>Code Owners</span>
        </div>
      </div>
      <div class="tool-card">
        <div class="tool-header">
          <div class="tool-icon">
            <i class="bx bx-infinite"></i>
          </div>
          <span class="tool-badge">CI/CD</span>
        </div>
        <div class="tool-title">Pipelines</div>
        <p class="tool-desc">Build, test, deploy with gated promotions.</p>
        <div class="tool-tags">
          <span>Artifacts</span><span>Canary</span><span>Blue/Green</span>
        </div>
      </div>
      <div class="tool-card">
        <div class="tool-header">
          <div class="tool-icon">
            <i class="bx bxl-docker"></i>
          </div>
          <span class="tool-badge">Containers</span>
        </div>
        <div class="tool-title">Docker</div>
        <p class="tool-desc">Lean images, SBOM, signed releases.</p>
        <div class="tool-tags">
          <span>Multi-Stage</span><span>Trivy</span><span>Signing</span>
        </div>
      </div>
      <div class="tool-card">
        <div class="tool-header">
          <div class="tool-icon">
            <i class="bx bx-box"></i>
          </div>
          <span class="tool-badge">Orchestration</span>
        </div>
        <div class="tool-title">Kubernetes</div>
        <p class="tool-desc">Secure workloads, autoscaling, resilient rollouts.</p>
        <div class="tool-tags">
          <span>Helm</span><span>HPA</span><span>Ingress</span>
        </div>
      </div>
      <div class="tool-card">
        <div class="tool-header">
          <div class="tool-icon">
            <i class="bx bx-code-block"></i>
          </div>
          <span class="tool-badge">IaC</span>
        </div>
        <div class="tool-title">Terraform</div>
        <p class="tool-desc">Reusable modules, drift detection, policy as code.</p>
        <div class="tool-tags">
          <span>Modules</span><span>Plan/Apply</span><span>OPA</span>
        </div>
      </div>
      <div class="tool-card">
        <div class="tool-header">
          <div class="tool-icon">
            <i class="bx bx-shield-quarter"></i>
          </div>
          <span class="tool-badge">Security</span>
        </div>
        <div class="tool-title">DevSecOps</div>
        <p class="tool-desc">Static analysis, dependency scans, secrets hygiene.</p>
        <div class="tool-tags">
          <span>SAST</span><span>DAST</span><span>Secrets Scan</span>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Implementation Process Section -->
<section class="py-20 bg-slate-950 border-t border-slate-800/60">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-indigo-500/10 border border-indigo-500/40 text-indigo-300 px-4 py-1 rounded-full text-sm font-medium mb-4">
        Our Approach
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        DevOps <span class="text-indigo-400">Implementation Process</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        We follow a structured approach to transform your organization's software delivery capabilities.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
      <!-- Step 1 -->
      <div class="relative">
        <div class="bg-slate-900/70 border border-slate-700/60 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
          <div class="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
            1
          </div>
          <div class="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center mb-6 mt-4">
            <i class="bx bx-analyse text-3xl text-white"></i>
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-3">Assessment</h3>
          <p class="text-gray-600">
            Evaluate current development processes, tools, and pain points to identify improvement opportunities.
          </p>
        </div>
      </div>

      <!-- Step 2 -->
      <div class="relative">
        <div class="bg-slate-900/70 border border-slate-700/60 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
          <div class="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-blue-600 to-cyan-700 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
            2
          </div>
          <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center mb-6 mt-4">
            <i class="bx bx-target-lock text-3xl text-white"></i>
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-3">Strategy</h3>
          <p class="text-gray-600">
            Develop a customized DevOps roadmap aligned with your business objectives and technical requirements.
          </p>
        </div>
      </div>

      <!-- Step 3 -->
      <div class="relative">
        <div class="bg-slate-900/70 border border-slate-700/60 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
          <div class="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-green-600 to-emerald-700 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
            3
          </div>
          <div class="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-6 mt-4">
            <i class="bx bx-cog text-3xl text-white"></i>
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-3">Implementation</h3>
          <p class="text-gray-600">
            Deploy tools, establish processes, and automate workflows to enable seamless collaboration.
          </p>
        </div>
      </div>

      <!-- Step 4 -->
      <div class="relative">
        <div class="bg-slate-900/70 border border-slate-700/60 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
          <div class="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-amber-600 to-orange-700 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
            4
          </div>
          <div class="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center mb-6 mt-4">
            <i class="bx bx-line-chart text-3xl text-white"></i>
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-3">Optimization</h3>
          <p class="text-gray-600">
            Continuously monitor, measure, and improve your DevOps practices for maximum efficiency.
          </p>
        </div>
      </div>
    </div>
  </div>
</section>

<section class="py-20 bg-slate-950 border-t border-slate-800/60">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 px-4 py-1 rounded-full text-sm font-medium mb-4">
        Key Outcomes
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Business <span class="text-emerald-400">Impact</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Accelerate product delivery with measurable improvements across reliability, security, and cost efficiency.
      </p>
    </div>
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
      <div class="bg-slate-900/70 rounded-xl shadow-lg p-8 border border-slate-700/60 text-center">
        <div class="text-4xl font-bold text-emerald-400 mb-2">3x</div>
        <div class="text-sm text-slate-300">Faster Releases</div>
      </div>
      <div class="bg-slate-900/70 rounded-xl shadow-lg p-8 border border-slate-700/60 text-center">
        <div class="text-4xl font-bold text-emerald-400 mb-2">99.99%</div>
        <div class="text-sm text-slate-300">Uptime</div>
      </div>
      <div class="bg-slate-900/70 rounded-xl shadow-lg p-8 border border-slate-700/60 text-center">
        <div class="text-4xl font-bold text-emerald-400 mb-2">-40%</div>
        <div class="text-sm text-slate-300">Infra Costs</div>
      </div>
      <div class="bg-slate-900/70 rounded-xl shadow-lg p-8 border border-slate-700/60 text-center">
        <div class="text-4xl font-bold text-emerald-400 mb-2">0</div>
        <div class="text-sm text-slate-300">Critical CVEs</div>
      </div>
    </div>
  </div>
</section>

<section class="py-20 bg-slate-950 border-t border-slate-800/60">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-indigo-500/10 border border-indigo-500/40 text-indigo-300 px-4 py-1 rounded-full text-sm font-medium mb-4">
        Engagement Packages
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Choose Your <span class="text-indigo-400">Model</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Flexible engagement options tailored to your team’s maturity and roadmap.
      </p>
    </div>
    <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
      <div class="bg-slate-900/70 rounded-2xl shadow-xl border border-slate-700/60 p-8">
        <div class="text-sm font-bold text-indigo-600 mb-3">Starter</div>
        <h3 class="text-xl font-bold text-white mb-3">Pipeline Readiness</h3>
        <ul class="space-y-2 text-slate-200 text-sm mb-6">
          <li class="flex items-center"><i class="bx bx-check text-green-500 mr-2"></i>CI setup and quality gates</li>
          <li class="flex items-center"><i class="bx bx-check text-green-500 mr-2"></i>Basic CD to staging</li>
          <li class="flex items-center"><i class="bx bx-check text-green-500 mr-2"></i>Security scanning</li>
        </ul>
        <a routerLink="/contact" class="inline-flex items-center px-5 py-3 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700">Contact Sales</a>
      </div>
      <div class="bg-slate-900/80 rounded-2xl shadow-xl border border-indigo-500/60 p-8 featured">
        <div class="text-sm font-bold text-indigo-600 mb-3">Professional</div>
        <h3 class="text-xl font-bold text-white mb-3">IaC + Multi-Env CD</h3>
        <ul class="space-y-2 text-slate-200 text-sm mb-6">
          <li class="flex items-center"><i class="bx bx-check text-green-500 mr-2"></i>Terraform baseline modules</li>
          <li class="flex items-center"><i class="bx bx-check text-green-500 mr-2"></i>Blue/Green or Canary deploys</li>
          <li class="flex items-center"><i class="bx bx-check text-green-500 mr-2"></i>Observability stack</li>
        </ul>
        <a routerLink="/contact" class="inline-flex items-center px-5 py-3 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700">Contact Sales</a>
      </div>
      <div class="bg-slate-900/70 rounded-2xl shadow-xl border border-slate-700/60 p-8">
        <div class="text-sm font-bold text-indigo-600 mb-3">Enterprise</div>
        <h3 class="text-xl font-bold text-white mb-3">Platform Engineering</h3>
        <ul class="space-y-2 text-slate-200 text-sm mb-6">
          <li class="flex items-center"><i class="bx bx-check text-green-500 mr-2"></i>Golden paths & templates</li>
          <li class="flex items-center"><i class="bx bx-check text-green-500 mr-2"></i>Governance & compliance</li>
          <li class="flex items-center"><i class="bx bx-check text-green-500 mr-2"></i>Cost optimization</li>
        </ul>
        <a routerLink="/contact" class="inline-flex items-center px-5 py-3 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700">Contact Sales</a>
      </div>
    </div>
  </div>
</section>

<section class="py-20 bg-slate-900 text-white">
  <div class="container mx-auto px-4">
    <div class="text-center mb-12">
      <div class="inline-block bg-white/10 text-white px-4 py-1 rounded-full text-sm font-semibold mb-4">
        SLA & Support
      </div>
      <h2 class="text-3xl md:text-4xl font-bold mb-4">
        Enterprise-Grade <span class="text-blue-300">Assurance</span>
      </h2>
      <p class="text-lg text-blue-100 max-w-3xl mx-auto">
        Backed by dedicated SREs, proactive monitoring, and strict security standards.
      </p>
    </div>
    <div class="grid grid-cols-2 md:grid-cols-4 gap-8">
      <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
        <div class="text-3xl font-bold text-white mb-2">99.99%</div>
        <div class="text-sm text-blue-100">Uptime SLA</div>
      </div>
      <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
        <div class="text-3xl font-bold text-white mb-2">24/7</div>
        <div class="text-sm text-blue-100">Support</div>
      </div>
      <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
        <div class="text-3xl font-bold text-white mb-2">30s</div>
        <div class="text-sm text-blue-100">Deploy Time</div>
      </div>
      <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
        <div class="text-3xl font-bold text-white mb-2">ISO</div>
        <div class="text-sm text-blue-100">Compliance</div>
      </div>
    </div>
    <div class="text-center mt-12">
      <a routerLink="/contact" class="inline-flex justify-center items-center px-8 py-4 text-base font-bold text-white transition-all duration-200 bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700">
        Talk to DevOps Experts
        <i class="bx bx-message-alt-detail ml-2 text-xl"></i>
      </a>
    </div>
  </div>
</section>

<!-- Benefits Section -->
<section class="py-20 bg-slate-950 border-t border-slate-800/60">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-green-500/10 border border-green-500/40 text-green-300 px-4 py-1 rounded-full text-sm font-medium mb-4">
        Key Benefits
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        DevOps <span class="text-green-400">Transformation Outcomes</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Experience measurable improvements in software delivery speed, quality, and team collaboration.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <!-- Benefit 1 -->
      <div class="bg-slate-900/70 rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-slate-700/60">
        <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-rocket text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Faster Time-to-Market</h3>
        <p class="text-slate-300">
          Reduce deployment cycles from weeks to minutes with automated pipelines and streamlined processes.
        </p>
      </div>

      <!-- Benefit 2 -->
      <div class="bg-slate-900/70 rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-slate-700/60">
        <div class="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-shield-alt-2 text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Improved Reliability</h3>
        <p class="text-slate-300">
          Ensure quality and stability of application updates with automated testing and monitoring.
        </p>
      </div>

      <!-- Benefit 3 -->
      <div class="bg-slate-900/70 rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-slate-700/60">
        <div class="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-expand-alt text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Enhanced Scalability</h3>
        <p class="text-slate-300">
          Scale applications and infrastructure seamlessly to meet growing user demands.
        </p>
      </div>

      <!-- Benefit 4 -->
      <div class="bg-slate-900/70 rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-slate-700/60">
        <div class="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-group text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Better Collaboration</h3>
        <p class="text-slate-300">
          Break down silos between development and operations teams for improved communication.
        </p>
      </div>

      <!-- Benefit 5 -->
      <div class="bg-slate-900/70 rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-slate-700/60">
        <div class="w-16 h-16 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-wrench text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Faster Issue Resolution</h3>
        <p class="text-slate-300">
          Quickly identify and resolve issues with comprehensive monitoring and shared responsibility.
        </p>
      </div>

      <!-- Benefit 6 -->
      <div class="bg-slate-900/70 rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-slate-700/60">
        <div class="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-trending-up text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Continuous Improvement</h3>
        <p class="text-slate-300">
          Iteratively enhance processes and systems based on feedback and performance metrics.
        </p>
      </div>
    </div>
  </div>
</section>

<!-- CTA Section -->
<section class="relative bg-gradient-to-br from-purple-600 via-indigo-700 to-blue-800 text-white overflow-hidden py-20">
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 right-0 w-96 h-96 bg-purple-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
    <div class="absolute bottom-0 left-0 w-96 h-96 bg-indigo-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
  </div>
  
  <div class="container mx-auto px-4 relative z-10">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div>
        <h2 class="text-3xl md:text-4xl font-bold mb-6">
          Ready to Transform Your Software Delivery?
        </h2>
        <p class="text-xl text-gray-200 mb-8">
          Start your DevOps journey today and experience faster deployments, improved reliability, and enhanced collaboration.
        </p>
        <div class="flex flex-col sm:flex-row gap-4">
          <a routerLink="/contact" class="bg-slate-800/30 text-white font-bold py-4 px-8 rounded-xl text-center hover:bg-slate-700/30 transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-2xl inline-flex items-center justify-center">
            <i class="bx bx-chat mr-2 text-xl"></i>
            <span class="text-lg">Get Started</span>
          </a>
          <a routerLink="/pricing" class="bg-transparent border-2 border-slate-700/50 text-white font-bold py-4 px-8 rounded-xl text-center hover:bg-slate-700/20 transition-all duration-300 inline-flex items-center justify-center">
            <i class="bx bx-purchase-tag mr-2 text-xl"></i>
            <span class="text-lg">View Pricing</span>
          </a>
        </div>
      </div>
      
      <div class="grid grid-cols-2 gap-6">
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">50%</div>
          <div class="text-gray-200">Faster Deployments</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">99.9%</div>
          <div class="text-gray-200">Uptime SLA</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">24/7</div>
          <div class="text-gray-200">Expert Support</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">100+</div>
          <div class="text-gray-200">Successful Projects</div>
        </div>
      </div>
    </div>
  </div>
</section>
